import { hideNotification, notify } from '@ps-int/ps-components/src/notifications/slice';
import { call, put, race, select, take } from 'typed-redux-saga';

import { NOTIFICATIONS_MAP } from '../../../../../shared/features/notifications/ps-notifications';
import { isMobileUa } from '../../../../../shared/features/uatraits/selectors';
import { toLoggableError } from '../../../../../shared/helpers/errors';
import { countMetrika } from '../../../../../shared/helpers/metrika';
import { PeersTabValue } from '../../../constants';
import {
    selectCanAdmit,
    selectCanAdmitAll,
    selectConferenceIsFull,
    selectConferenceIsOverflow,
    selectHasNewWaitingRoomPeers,
    selectIsWaitingRoomOverflow,
    selectIsWaitingRoomTab,
    selectSortedWaitingRoomPeers,
} from '../../../selectors';
import { NotificationId, SidebarContent } from '../../../types';
import { participantsAddedAction, participantsRemovedAction } from '../../conference/slice';
import { setPeersTab, setSidebarContent } from '../../sidebar/slice';
import { logger } from '../helpers/logger';
import { setHasNewWaitingRoomPeers, showWaitingRoomNotificationsAction } from '../slice';

export function* watchParticipantsAndShowNotifications() {
    while (true) {
        yield* race([
            take(participantsAddedAction),
            take(participantsRemovedAction),
            take(showWaitingRoomNotificationsAction),
        ]);

        yield* call(showWaitingRoomNotifications);
    }
}

function* showWaitingRoomNotifications() {
    const isWaitingRoomTab = yield* select(selectIsWaitingRoomTab);
    const canAdmit = yield* select(selectCanAdmit);
    const canAdmitAll = yield* select(selectCanAdmitAll);

    if (!canAdmit && !canAdmitAll) {
        yield* call(hideAllWaitingRoomNotifications);
        return;
    }

    yield* call(showOrHideLimitNotification);

    if (isWaitingRoomTab) {
        yield* call(hideAdmitNotifications);
        return;
    }

    const peers = yield* select(selectSortedWaitingRoomPeers);
    const isMobile = yield* select(isMobileUa);
    const isOverflow = yield* select(selectIsWaitingRoomOverflow);
    const isConferenceFull = yield* select(selectConferenceIsFull);
    const hasNewWaitingPeers = yield* select(selectHasNewWaitingRoomPeers);
    const hasNewPeersAndNotFull = !isConferenceFull && hasNewWaitingPeers;

    yield* call(hideAdmitNotifications);
    if (isOverflow && hasNewPeersAndNotFull) {
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.WAITING_ROOM_OVERFLOW](!isMobile)));
        yield* call(countMetrika, 'waiting_rooms', 'popup_actions', 'limit_reached_show');
        return;
    }

    if (hasNewPeersAndNotFull) {
        if (peers.length === 1 && canAdmit) {
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.WAITING_ROOM_NEW_PEER](peers[0])));
            const { uid } = peers[0];
            if (uid) {
                yield* call(countMetrika, 'waiting_rooms', 'popup_actions', 'authorised_waiting_show');
            } else {
                yield* call(countMetrika, 'waiting_rooms', 'popup_actions', 'guest_waiting_show');
            }
        } else if (canAdmitAll) {
            yield* put(notify(NOTIFICATIONS_MAP[NotificationId.WAITING_ROOM_NEW_PEERS](!isMobile)));
            yield* call(countMetrika, 'waiting_rooms', 'popup_actions', 'people_waiting_show');
        }
    }
}

function* showOrHideLimitNotification() {
    const conferenceIsOverflow = yield* select(selectConferenceIsOverflow);

    if (conferenceIsOverflow) {
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.WAITING_ROOM_CONFERENCE_LIMIT]()));
    } else {
        yield* put(hideNotification(NotificationId.WAITING_ROOM_CONFERENCE_LIMIT));
    }
}

export function* showWaitingRoomSidebarSaga() {
    try {
        yield* put(setPeersTab(PeersTabValue.WaitingRoom));
        yield* put(setSidebarContent(SidebarContent.Participants));
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'showWaitingRoomSidebarSaga',
            })
        );
    }
}

export function* resetHasNewWaitingRoomPeersSaga() {
    try {
        const isWaitingRoomTab = yield* select(selectIsWaitingRoomTab);
        if (isWaitingRoomTab) {
            yield* put(setHasNewWaitingRoomPeers(false));
            yield* call(hideAdmitNotifications);
        }
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'resetHasNewWaitingRoomPeersSaga',
            })
        );
    }
}

function* hideAllWaitingRoomNotifications() {
    yield* call(hideAdmitNotifications);
    yield* put(hideNotification(NotificationId.WAITING_ROOM_CONFERENCE_LIMIT));
}

function* hideAdmitNotifications() {
    yield* put(hideNotification(NotificationId.WAITING_ROOM_OVERFLOW));
    yield* put(hideNotification(NotificationId.WAITING_ROOM_NEW_PEER));
    yield* put(hideNotification(NotificationId.WAITING_ROOM_NEW_PEERS));
}
